import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  FunnelIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  TableCellsIcon,
  CalendarIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon,
  HomeIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { clutchesApi } from '../api/clutches';
import type { Clutch } from '../api/clutches';
import { breedingApi } from '../api/breeding';
import type { BreedingPair } from '../api/breeding';
import { format } from 'date-fns';
import ViewClutchModal from '../components/ViewClutchModal';
import ManageEggsModal from '../components/ManageEggsModal';
import BreedingPairCombobox from '../components/BreedingPairCombobox';
import { EggStatus } from '../utils/enums';
import { getBirdDisplayId } from '../utils/birdDisplay';
import PageHero from '../components/ui/PageHero';
import EmptyState from '../components/ui/EmptyState';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../components/ui/BrandModal';
import { daysAgo, type ClutchListFilter } from '../utils/listFilterPaths';

interface ClutchFilters {
  status?: 'active' | 'hatched' | 'failed';
  breedingPairId?: string;
  focus?: ClutchListFilter;
  dateRange?: {
    start: string;
    end: string;
  };
  eggCount?: {
    min: number;
    max: number;
  };
}

export default function Clutches() {
  // Data state
  const [clutches, setClutches] = useState<Clutch[]>([]);
  const [breedingPairs, setBreedingPairs] = useState<BreedingPair[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<ClutchFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [sortBy, setSortBy] = useState<keyof Clutch>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showManageEggsModal, setShowManageEggsModal] = useState(false);
  const [selectedClutch, setSelectedClutch] = useState<Clutch | null>(null);
  const [newClutchPairId, setNewClutchPairId] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const focus = searchParams.get('filter') as ClutchListFilter | null;
    const status = searchParams.get('status') as ClutchFilters['status'] | null;
    const next: ClutchFilters = {};
    if (focus === 'recent' || focus === 'thisYear') {
      next.focus = focus;
    }
    if (status === 'active' || status === 'hatched' || status === 'failed') {
      next.status = status;
    }
    if (Object.keys(next).length > 0) {
      setFilters(next);
      setShowFilters(true);
      if (focus === 'recent' || focus === 'thisYear') {
        setSortBy('date');
        setSortOrder('desc');
      }
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [clutchesResponse, pairsResponse] = await Promise.all([
        clutchesApi.getAll(),
        breedingApi.getAll(),
      ]);
      setClutches(clutchesResponse.data);
      setBreedingPairs(pairsResponse.data);
      setError(null);
    } catch (err) {
      setError('Failed to load clutch data');
      console.error('Error loading clutch data:', err);
      toast.error('Failed to load clutch data');
    } finally {
      setLoading(false);
    }
  };

  // Filter and search logic
  const filteredClutches = useMemo(() => {
    let result = clutches;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (clutch) =>
          (clutch.breeding_pair?.male_bird && getBirdDisplayId(clutch.breeding_pair.male_bird).toLowerCase().includes(term)) ||
          (clutch.breeding_pair?.female_bird && getBirdDisplayId(clutch.breeding_pair.female_bird).toLowerCase().includes(term)) ||
          clutch.breeding_pair?.cage_number?.toLowerCase().includes(term) ||
          clutch.id.toLowerCase().includes(term) ||
          format(new Date(clutch.date), 'MMM dd, yyyy').toLowerCase().includes(term)
      );
    }

    // Apply filters
    if (filters.status) {
      result = result.filter((clutch) => {
        const eggStatuses = clutch.eggs?.map(egg => egg.status) || [];
        switch (filters.status) {
          case 'active':
            return eggStatuses.some(status => 
              status === EggStatus.LAID || 
              status === EggStatus.INCUBATING
            );
          case 'hatched':
            return eggStatuses.some(status => status === EggStatus.HATCHED);
          case 'failed':
            return eggStatuses.every(status => 
              status === EggStatus.NOT_FERTILIZED ||
              status === EggStatus.BROKEN ||
              status === EggStatus.ABANDONED ||
              status === EggStatus.DEAD_IN_SHELL ||
              status === EggStatus.DEAD_BEFORE_BANDING
            );
          default:
            return true;
        }
      });
    }

    if (filters.breedingPairId) {
      result = result.filter((clutch) => clutch.breedingPairId === filters.breedingPairId);
    }

    if (filters.dateRange) {
      result = result.filter((clutch) => {
        const clutchDate = new Date(clutch.date);
        const startDate = new Date(filters.dateRange!.start);
        const endDate = new Date(filters.dateRange!.end);
        return clutchDate >= startDate && clutchDate <= endDate;
      });
    }

    if (filters.eggCount) {
      result = result.filter((clutch) => {
        const eggCount = clutch.eggs?.length || 0;
        return eggCount >= filters.eggCount!.min && eggCount <= filters.eggCount!.max;
      });
    }

    if (filters.focus === 'recent') {
      const cutoff = daysAgo(90);
      result = result.filter((clutch) => new Date(clutch.date) >= cutoff);
    } else if (filters.focus === 'thisYear') {
      const year = new Date().getFullYear();
      result = result.filter((clutch) => new Date(clutch.date).getFullYear() === year);
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      // Handle undefined values
      if (aVal === undefined && bVal === undefined) return 0;
      if (aVal === undefined) return sortOrder === 'asc' ? -1 : 1;
      if (bVal === undefined) return sortOrder === 'asc' ? 1 : -1;
      
      // Convert to strings for comparison if they aren't comparable directly
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      } else {
        // For non-string values, convert to string for comparison
        const aStr = String(aVal);
        const bStr = String(bVal);
        if (aStr < bStr) return sortOrder === 'asc' ? -1 : 1;
        if (aStr > bStr) return sortOrder === 'asc' ? 1 : -1;
      }
      
      return 0;
    });

    return result;
  }, [clutches, searchTerm, filters, sortBy, sortOrder]);

  // Pagination logic
  const totalPages = Math.ceil(filteredClutches.length / itemsPerPage);
  const paginatedClutches = filteredClutches.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Event handlers
  const handleAddClutch = async (breedingPairId: string, date: string) => {
    try {
      await clutchesApi.create({ 
        date,
        breedingPairId,
        status: 'ACTIVE'
      });
      await loadData();
      setShowAddModal(false);
      setNewClutchPairId('');
      toast.success('Clutch added successfully');
    } catch (error) {
      console.error('Error adding clutch:', error);
      toast.error('Failed to add clutch');
    }
  };

  const handleDeleteClutch = async () => {
    if (!selectedClutch) return;
    
    try {
      await clutchesApi.delete(selectedClutch.id);
      await loadData();
      setShowDeleteModal(false);
      setSelectedClutch(null);
      toast.success('Clutch deleted successfully');
    } catch (error) {
      console.error('Error deleting clutch:', error);
      toast.error('Failed to delete clutch');
    }
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setCurrentPage(1);
    setSearchParams({});
  };

  const focusLabel =
    filters.focus === 'recent'
      ? 'Recent clutches (last 90 days)'
      : filters.focus === 'thisYear'
        ? `Clutches in ${new Date().getFullYear()}`
        : filters.status === 'hatched'
          ? 'Clutches with hatched eggs'
          : filters.status === 'active'
            ? 'Active clutches'
            : filters.status === 'failed'
              ? 'Failed clutches'
              : null;

  const openViewModal = (clutch: Clutch) => {
    setSelectedClutch(clutch);
    setShowViewModal(true);
  };

  const openDeleteModal = (clutch: Clutch) => {
    setSelectedClutch(clutch);
    setShowDeleteModal(true);
  };

  const openManageEggsModal = (clutch: Clutch) => {
    setSelectedClutch(clutch);
    setShowManageEggsModal(true);
  };

  const getStatusBadge = (clutch: Clutch) => {
    const eggStatuses = clutch.eggs?.map(egg => egg.status) || [];
    
    if (eggStatuses.length === 0) {
      return <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">No Eggs</span>;
    }

    if (eggStatuses.some(status => status === EggStatus.HATCHED)) {
      return <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">Hatched</span>;
    }

    if (eggStatuses.some(status => status === EggStatus.LAID || status === EggStatus.INCUBATING)) {
      return <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-cyan-800">Active</span>;
    }

    if (eggStatuses.every(status => 
      status === EggStatus.NOT_FERTILIZED ||
      status === EggStatus.BROKEN ||
      status === EggStatus.ABANDONED ||
      status === EggStatus.DEAD_IN_SHELL ||
      status === EggStatus.DEAD_BEFORE_BANDING
    )) {
      return <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">Failed</span>;
    }

    return <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">Mixed</span>;
  };

  const getPairSpecies = (clutch: Clutch) => {
    const maleSpecies = clutch.breeding_pair?.male_bird?.species?.commonName;
    const femaleSpecies = clutch.breeding_pair?.female_bird?.species?.commonName;
    
    if (maleSpecies === femaleSpecies) {
      return maleSpecies || 'Unknown species';
    }
    
    if (maleSpecies && femaleSpecies) {
      return `${maleSpecies} × ${femaleSpecies}`;
    }
    
    return maleSpecies || femaleSpecies || 'Unknown species';
  };

  const getClutchStats = () => {
    const totalClutches = filteredClutches.length;
    const activeClutches = filteredClutches.filter(clutch => 
      clutch.eggs?.some(egg => 
        egg.status === EggStatus.LAID || egg.status === EggStatus.INCUBATING
      )
    ).length;
    const hatchedClutches = filteredClutches.filter(clutch => 
      clutch.eggs?.some(egg => egg.status === EggStatus.HATCHED)
    ).length;
    const totalEggs = filteredClutches.reduce((sum, clutch) => sum + (clutch.eggs?.length || 0), 0);

    return { totalClutches, activeClutches, hatchedClutches, totalEggs };
  };

  const stats = getClutchStats();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/80">
        <div className="py-10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" />
              <p className="mt-2 text-sm text-slate-500">Loading clutches...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50/80">
        <div className="py-10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
              <button
                onClick={loadData}
                className="mt-2 text-sm font-medium text-red-600 hover:text-red-500"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/80">
      <div className="py-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <PageHero
            title="Clutches"
            subtitle="Monitor and manage clutches and their eggs from your breeding pairs"
            actions={
              <button type="button" onClick={() => setShowAddModal(true)} className="btn btn-primary focus-pop">
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                Add Clutch
              </button>
            }
          />

          {focusLabel && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-md border border-cyan-200 bg-cyan-50 px-4 py-2 text-sm text-cyan-900">
              <span>
                Showing: <span className="font-semibold">{focusLabel}</span>
              </span>
              <button type="button" onClick={clearFilters} className="font-medium text-cyan-700 hover:text-cyan-900">
                Clear filter
              </button>
            </div>
          )}

          {/* Stats */}
          <div className="mt-8">
            <dl className="grid grid-cols-1 gap-5 sm:grid-cols-4">
              <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
                <dt className="truncate text-sm font-medium text-slate-500">Total Clutches</dt>
                <dd className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">{stats.totalClutches}</dd>
              </div>
              <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
                <dt className="truncate text-sm font-medium text-slate-500">Active Clutches</dt>
                <dd className="mt-1 text-3xl font-semibold tracking-tight text-cyan-600">{stats.activeClutches}</dd>
              </div>
              <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
                <dt className="truncate text-sm font-medium text-slate-500">Hatched Clutches</dt>
                <dd className="mt-1 text-3xl font-semibold tracking-tight text-green-600">{stats.hatchedClutches}</dd>
              </div>
              <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
                <dt className="truncate text-sm font-medium text-slate-500">Total Eggs</dt>
                <dd className="mt-1 text-3xl font-semibold tracking-tight text-purple-600">{stats.totalEggs}</dd>
              </div>
            </dl>
          </div>

          {/* Search and filters */}
          <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-1 items-center gap-x-4">
              <div className="flex items-center flex-1 max-w-md rounded-md ring-1 ring-inset ring-gray-300 bg-white focus-within:ring-2 focus-within:ring-inset focus-within:ring-cyan-500">
                <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400" aria-hidden="true">
                  <MagnifyingGlassIcon className="h-5 w-5" />
                </span>
                <input
                  type="text"
                  placeholder="Search clutches..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1 min-w-0 py-1.5 pr-3 border-0 bg-transparent text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0 sm:text-sm sm:leading-6"
                />
              </div>
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80"
              >
                <FunnelIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                Filters
                {Object.keys(filters).length > 0 && (
                  <span className="ml-1 rounded-full bg-cyan-600 px-2 py-0.5 text-xs text-white">
                    {Object.keys(filters).length}
                  </span>
                )}
              </button>
            </div>
            <div className="flex items-center gap-x-4">
              {/* Sort */}
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSortBy(field as keyof Clutch);
                  setSortOrder(order as 'asc' | 'desc');
                }}
                className="rounded-md border-0 py-1.5 pl-3 pr-10 text-slate-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-cyan-500 sm:text-sm sm:leading-6"
              >
                <option key="date-desc" value="date-desc">Newest First</option>
                <option key="date-asc" value="date-asc">Oldest First</option>
                <option key="breedingPairId-asc" value="breedingPairId-asc">Breeding Pair A-Z</option>
                <option key="breedingPairId-desc" value="breedingPairId-desc">Breeding Pair Z-A</option>
              </select>
              
              {/* View mode toggle */}
              <div className="flex rounded-md shadow-sm">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-10 ${
                    viewMode === 'grid'
                      ? 'bg-cyan-600 text-white hover:bg-cyan-500'
                      : 'bg-white text-slate-900'
                  }`}
                >
                  <Squares2X2Icon className="h-5 w-5" aria-hidden="true" />
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`relative -ml-px inline-flex items-center rounded-r-md px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-10 ${
                    viewMode === 'table'
                      ? 'bg-cyan-600 text-white hover:bg-cyan-500'
                      : 'bg-white text-slate-900'
                  }`}
                >
                  <TableCellsIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="mt-4 rounded-lg bg-white p-6 shadow">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700">Status</label>
                  <select
                    value={filters.status || ''}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value as any })}
                    className="mt-1 input text-sm"
                  >
                    <option key="all-status" value="">All Status</option>
                    <option key="active" value="active">Active</option>
                    <option key="hatched" value="hatched">Hatched</option>
                    <option key="failed" value="failed">Failed</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700">Breeding Pair</label>
                  <BreedingPairCombobox
                    pairs={breedingPairs}
                    value={filters.breedingPairId || ''}
                    onChange={(pairId) =>
                      setFilters({ ...filters, breedingPairId: pairId || undefined })
                    }
                    allowClear
                    placeholder="Search or filter by pair…"
                    emptyMessage="No breeding pairs found"
                    optionsMaxHeightClass="max-h-48"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700">Date From</label>
                  <input
                    type="date"
                    value={filters.dateRange?.start || ''}
                    onChange={(e) => setFilters({
                      ...filters,
                      dateRange: { ...filters.dateRange, start: e.target.value, end: filters.dateRange?.end || '' }
                    })}
                    className="input mt-1 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700">Date To</label>
                  <input
                    type="date"
                    value={filters.dateRange?.end || ''}
                    onChange={(e) => setFilters({
                      ...filters,
                      dateRange: { ...filters.dateRange, start: filters.dateRange?.start || '', end: e.target.value }
                    })}
                    className="input mt-1 sm:text-sm"
                  />
                </div>
              </div>
              
              <div className="mt-4 flex justify-end gap-x-3">
                <button
                  type="button"
                  onClick={clearFilters}
                  className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80"
                >
                  <XMarkIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                  Clear Filters
                </button>
              </div>
            </div>
          )}

          {/* Content */}
          {paginatedClutches.length === 0 ? (
            <EmptyState
              title="No clutches found"
              description={
                searchTerm || Object.keys(filters).length > 0
                  ? 'Try adjusting your search or filters.'
                  : 'Get started by adding your first clutch.'
              }
              icon={<HomeIcon className="h-6 w-6" />}
              action={
                !searchTerm && Object.keys(filters).length === 0 ? (
                  <button type="button" onClick={() => setShowAddModal(true)} className="btn btn-primary focus-pop">
                    <PlusIcon className="h-5 w-5" />
                    Add Clutch
                  </button>
                ) : undefined
              }
            />
          ) : (
            <>
              {/* Grid View */}
              {viewMode === 'grid' && (
                <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {paginatedClutches.map((clutch) => (
                    <div
                      key={clutch.id}
                      className="overflow-hidden rounded-lg bg-white shadow hover:shadow-md transition-shadow duration-200"
                    >
                      <div className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <HomeIcon className="h-8 w-8 text-cyan-600" />
                            <div className="ml-3">
                              <h3 className="text-sm font-semibold text-slate-900">
                                {format(new Date(clutch.date), 'MMM dd, yyyy')}
                              </h3>
                              <p className="text-sm text-slate-500">
                                {clutch.breeding_pair?.male_bird ? getBirdDisplayId(clutch.breeding_pair.male_bird) : 'Unknown'} × {clutch.breeding_pair?.female_bird ? getBirdDisplayId(clutch.breeding_pair.female_bird) : 'Unknown'}
                              </p>
                            </div>
                          </div>
                          {getStatusBadge(clutch)}
                        </div>
                        
                        <div className="mt-4">
                          <dl className="grid grid-cols-2 gap-4">
                            <div>
                              <dt className="text-sm font-medium text-slate-500">Cage</dt>
                              <dd className="text-sm text-slate-900">{clutch.breeding_pair?.cage_number || 'N/A'}</dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-slate-500">Eggs</dt>
                              <dd className="text-sm text-slate-900">{clutch.eggs?.length || 0}</dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-slate-500">Days to Hatch</dt>
                              <dd className="text-sm text-slate-900">
                                {clutch.daysRemainingTillHatching !== null && clutch.daysRemainingTillHatching !== undefined ? (
                                  <span className={`font-semibold ${
                                    clutch.daysRemainingTillHatching <= 0 
                                      ? 'text-red-600' 
                                      : clutch.daysRemainingTillHatching <= 7 
                                        ? 'text-orange-600' 
                                        : 'text-green-600'
                                  }`}>
                                    {clutch.daysRemainingTillHatching <= 0 
                                      ? `${Math.abs(clutch.daysRemainingTillHatching)} days overdue` 
                                      : `${clutch.daysRemainingTillHatching} days`
                                    }
                                  </span>
                                ) : (
                                  <span className="text-slate-400">N/A</span>
                                )}
                              </dd>
                            </div>
                          </dl>
                        </div>

                        <div className="mt-4">
                          <p className="text-sm text-slate-500">{getPairSpecies(clutch)}</p>
                        </div>

                        <div className="mt-6 flex justify-between">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => openViewModal(clutch)}
                              className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => openManageEggsModal(clutch)}
                              className="inline-flex items-center rounded-md bg-cyan-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-cyan-500"
                            >
                              <ClipboardDocumentListIcon className="h-4 w-4" />
                            </button>
                          </div>
                          <button
                            onClick={() => openDeleteModal(clutch)}
                            className="inline-flex items-center rounded-md bg-red-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Table View */}
              {viewMode === 'table' && (
                <div className="mt-8 flow-root">
                  <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                      <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                        <table className="min-w-full divide-y divide-gray-300">
                          <thead className="bg-slate-50/80">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                Date
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                Breeding Pair
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                Cage
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                Eggs
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                Days to Hatch
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                Status
                              </th>
                              <th className="relative px-6 py-3">
                                <span className="sr-only">Actions</span>
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 bg-white">
                            {paginatedClutches.map((clutch) => (
                              <tr key={clutch.id} className="hover:bg-slate-50/80">
                                <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-900">
                                  {format(new Date(clutch.date), 'MMM dd, yyyy')}
                                </td>
                                <td className="px-6 py-4 text-sm text-slate-900">
                                  <div>
                                    <div className="font-medium">
                                      {clutch.breeding_pair?.male_bird ? getBirdDisplayId(clutch.breeding_pair.male_bird) : 'Unknown'} × {clutch.breeding_pair?.female_bird ? getBirdDisplayId(clutch.breeding_pair.female_bird) : 'Unknown'}
                                    </div>
                                    <div className="text-slate-500">{getPairSpecies(clutch)}</div>
                                  </div>
                                </td>
                                <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-900">
                                  {clutch.breeding_pair?.cage_number || 'N/A'}
                                </td>
                                <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-900">
                                  {clutch.eggs?.length || 0}
                                </td>
                                <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-900">
                                  {clutch.daysRemainingTillHatching !== null && clutch.daysRemainingTillHatching !== undefined ? (
                                    <span className={`font-semibold ${
                                      clutch.daysRemainingTillHatching <= 0 
                                        ? 'text-red-600' 
                                        : clutch.daysRemainingTillHatching <= 7 
                                          ? 'text-orange-600' 
                                          : 'text-green-600'
                                    }`}>
                                      {clutch.daysRemainingTillHatching <= 0 
                                        ? `${Math.abs(clutch.daysRemainingTillHatching)} days overdue` 
                                        : `${clutch.daysRemainingTillHatching} days`
                                      }
                                    </span>
                                  ) : (
                                    <span className="text-slate-400">N/A</span>
                                  )}
                                </td>
                                <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-900">
                                  {getStatusBadge(clutch)}
                                </td>
                                <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                  <div className="flex justify-end space-x-2">
                                    <button
                                      onClick={() => openViewModal(clutch)}
                                      className="text-cyan-600 hover:text-indigo-900"
                                      title="View details"
                                    >
                                      <EyeIcon className="h-5 w-5" />
                                    </button>
                                    <button
                                      onClick={() => openManageEggsModal(clutch)}
                                      className="text-cyan-600 hover:text-indigo-900"
                                      title="Manage eggs"
                                    >
                                      <ClipboardDocumentListIcon className="h-5 w-5" />
                                    </button>
                                    <button
                                      onClick={() => openDeleteModal(clutch)}
                                      className="text-red-600 hover:text-red-900"
                                      title="Delete clutch"
                                    >
                                      <TrashIcon className="h-5 w-5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-8 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                  <div className="flex flex-1 justify-between sm:hidden">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-slate-700">
                        Showing{' '}
                        <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span>
                        {' '}to{' '}
                        <span className="font-medium">
                          {Math.min(currentPage * itemsPerPage, filteredClutches.length)}
                        </span>
                        {' '}of{' '}
                        <span className="font-medium">{filteredClutches.length}</span>
                        {' '}results
                      </p>
                    </div>
                    <div>
                      <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                        <button
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center rounded-l-md px-2 py-2 text-slate-400 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Previous</span>
                          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                        
                        {[...Array(totalPages)].map((_, i) => {
                          const page = i + 1;
                          const isCurrentPage = page === currentPage;
                          
                          if (
                            page === 1 ||
                            page === totalPages ||
                            (page >= currentPage - 2 && page <= currentPage + 2)
                          ) {
                            return (
                              <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                  isCurrentPage
                                    ? 'z-10 bg-cyan-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-600'
                                    : 'text-slate-900 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:outline-offset-0'
                                } focus:z-20`}
                              >
                                {page}
                              </button>
                            );
                          }
                          
                          if (page === currentPage - 3 || page === currentPage + 3) {
                            return (
                              <span key={page} className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-slate-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0">
                                ...
                              </span>
                            );
                          }
                          
                          return null;
                        })}
                        
                        <button
                          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center rounded-r-md px-2 py-2 text-slate-400 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Next</span>
                          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* View Clutch Modal */}
      {selectedClutch && (
        <ViewClutchModal
          isOpen={showViewModal}
          onClose={() => setShowViewModal(false)}
          clutch={selectedClutch}
        />
      )}

      {/* Manage Eggs Modal */}
      {selectedClutch && (
        <ManageEggsModal
          isOpen={showManageEggsModal}
          onClose={() => setShowManageEggsModal(false)}
          clutch={selectedClutch}
          onUpdate={loadData}
        />
      )}

      {/* Add Clutch Modal */}
      <BrandModal
        open={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setNewClutchPairId('');
        }}
        maxWidthClass="max-w-lg"
      >
        <BrandModalHeader
          title="Add New Clutch"
          onClose={() => {
            setShowAddModal(false);
            setNewClutchPairId('');
          }}
        />
        <form
          id="add-clutch-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!newClutchPairId) {
              toast.error('Select a breeding pair');
              return;
            }
            const formData = new FormData(e.currentTarget);
            const date = formData.get('date') as string;
            handleAddClutch(newClutchPairId, date);
          }}
        >
          <BrandModalBody>
            <div className="space-y-4">
              <div>
                <label htmlFor="add-clutch-pair" className="block text-sm font-medium text-slate-700">
                  Breeding Pair
                </label>
                <BreedingPairCombobox
                  id="add-clutch-pair"
                  pairs={breedingPairs}
                  value={newClutchPairId}
                  onChange={setNewClutchPairId}
                  required
                  placeholder="Search by band, cage, or species…"
                  emptyMessage="No breeding pairs yet — create one in Breeding first"
                  optionsMaxHeightClass="max-h-72"
                />
                <p className="mt-1 text-xs text-slate-500">
                  {breedingPairs.length} pair
                  {breedingPairs.length === 1 ? '' : 's'} available
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Clutch Date</label>
                <input
                  type="date"
                  name="date"
                  required
                  defaultValue={format(new Date(), 'yyyy-MM-dd')}
                  className="input mt-1 sm:text-sm"
                />
              </div>
            </div>
          </BrandModalBody>
          <BrandModalFooter>
            <button
              type="button"
              onClick={() => {
                setShowAddModal(false);
                setNewClutchPairId('');
              }}
              className="btn btn-secondary text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="add-clutch-form"
              className="btn btn-primary text-sm"
              disabled={!newClutchPairId}
            >
              Add Clutch
            </button>
          </BrandModalFooter>
        </form>
      </BrandModal>

      {/* Delete Confirmation Modal */}
      <BrandModal open={showDeleteModal} onClose={() => setShowDeleteModal(false)} maxWidthClass="max-w-md">
        <BrandModalHeader title="Delete Clutch" onClose={() => setShowDeleteModal(false)} />
        <BrandModalBody>
          <p className="text-sm text-slate-600">
            Are you sure you want to delete this clutch from {selectedClutch && format(new Date(selectedClutch.date), 'MMM dd, yyyy')}?
            This action cannot be undone and will also delete all eggs in this clutch.
          </p>
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            onClick={() => setShowDeleteModal(false)}
            className="btn btn-secondary text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDeleteClutch}
            className="btn btn-danger text-sm"
          >
            Delete
          </button>
        </BrandModalFooter>
      </BrandModal>
    </div>
  );
} 